<template>
  <div class="d-flex justify-end align-center">


    <!-- Widgets (hidden during semantic search — group_by not supported) -->
    <action-menu v-if="isWorks && !isSemanticSearch" action="group_by">
      <template #activator="{ props: menuProps }">
        <v-tooltip text="Widgets" location="bottom" aria-label="Widgets">
          <template #activator="{ props: tooltipProps }">
            <v-btn icon variant="text" v-bind="{ ...menuProps, ...tooltipProps }" aria-label="Widgets">
              <v-icon color="grey-darken-1">mdi-widgets</v-icon>
            </v-btn>
          </template>
        </v-tooltip>
      </template>
    </action-menu>

    <!-- Three-dots menu -->
    <v-menu location="bottom" v-model="isDotsMenuOpen">
      <template #activator="{ props }">
        <v-btn icon variant="text" v-bind="props" aria-label="More options">
          <v-icon color="grey-darken-1">mdi-dots-vertical</v-icon>
        </v-btn>
      </template>
      <v-list min-width="260">
        <!-- View toggle: shown for ALL entity types. Table view (and per-entity
             columns) now works everywhere (#304), and the export dialog's
             "Open in table view" can land non-works users in table view — so they
             need this toggle to get back to list view. (Was works-only from the
             #295 era when table view was works-only.) -->
        <v-list-item @click="handleToggleTableView">
          <template #prepend>
            <v-icon>{{ url.isTableView($route) ? 'mdi-format-list-checkbox' : 'mdi-table' }}</v-icon>
          </template>
          <v-list-item-title>{{ url.isTableView($route) ? 'View as list' : 'View as table' }}</v-list-item-title>
        </v-list-item>

        <!-- Page size: hover submenu picking results per page. Always enabled —
             table view auto-selects 100 but you can still drop to 10/20/50. -->
        <v-menu
          submenu
          open-on-hover
          location="end"
          :offset="2"
        >
          <template #activator="{ props: subProps }">
            <!-- Drop the activator's click-toggle and swallow the click: the
                 submenu opens on hover, so a click here should do nothing — not
                 dismiss the kebab (Vuetify menus close on content click). Other
                 rows keep their default click-to-act-and-close behavior. -->
            <v-list-item
              v-bind="{ ...subProps, onClick: undefined }"
              @click.stop
            >
              <template #prepend>
                <v-icon>mdi-counter</v-icon>
              </template>
              <v-list-item-title>Page size</v-list-item-title>
              <template #append>
                <span class="text-medium-emphasis mr-2">{{ url.getPerPage() }}</span>
                <v-icon size="20">mdi-chevron-right</v-icon>
              </template>
            </v-list-item>
          </template>
          <v-list density="compact" min-width="120">
            <v-list-item
              v-for="size in url.pageSizeOptions"
              :key="size"
              @click="url.setPerPage(size)"
            >
              <template #prepend>
                <v-icon
                  size="18"
                  :style="{ visibility: size === url.getPerPage() ? 'visible' : 'hidden' }"
                >mdi-check</v-icon>
              </template>
              <v-list-item-title>{{ size }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>

        <v-list-item @click="openQrCode">
          <template #prepend>
            <v-icon>mdi-qrcode</v-icon>
          </template>
          <v-list-item-title>Get QR code</v-list-item-title>
        </v-list-item>

        <!-- Read-only "Show as OQL" toggle (oxjob #346) — behind the `oql`
             feature flag, so it ships dark. Toggles the OQL render panel in the
             right column; parent (ExpertSerp) owns + persists the state. -->
        <template v-if="oqlFlag">
          <v-divider />
          <v-list-item @click="handleToggleOql">
            <template #prepend>
              <v-icon>{{ showOql ? 'mdi-check' : 'mdi-code-tags' }}</v-icon>
            </template>
            <v-list-item-title>{{ showOql ? 'Hide OQL' : 'Show OQL' }}</v-list-item-title>
          </v-list-item>
        </template>
      </v-list>
    </v-menu>

    <!-- QR Code dialog -->
    <v-dialog :width="qrCodeSize" v-model="isDialogOpen.qrCode">
      <v-card rounded>
        <v-toolbar flat>
          <v-toolbar-title>QR code for this page:</v-toolbar-title>
          <v-spacer/>
        </v-toolbar>
        <v-card-text v-if="isUrlTooBigForQR">
          <v-alert type="warning" text>
            Your current URL is too long to create a QR code.
          </v-alert>
        </v-card-text>
        <qrcode-vue v-else :value="urlToShare" :size="qrCodeSize" />
        <v-card-actions>
          <v-spacer/>
          <v-btn color="primary" rounded @click="isDialogOpen.qrCode = false">Dismiss</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>


<script setup>
import { ref, computed, reactive } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import { useDisplay } from 'vuetify';
import QrcodeVue from 'qrcode.vue';

import { url } from '@/url';

import ActionMenu from '@/components/Action/ActionMenu.vue';


defineOptions({ name: 'SerpRightToolbar' });

const props = defineProps({
  resultsObject: Object,
  showOql: Boolean,
});

const emit = defineEmits(['toggle-oql']);

const store = useStore();
const route = useRoute();
const { mdAndUp } = useDisplay();

const isDotsMenuOpen = ref(false);
const isDialogOpen = reactive({
  qrCode: false,
});

// Store getters
const entityType = computed(() => store.getters.entityType);
const isWorks = computed(() => entityType.value === 'works');
const isSemanticSearch = computed(() => !!route.query['search.semantic']);
const oqlFlag = computed(() => !!store.getters.featureFlags['oql']);

// QR code
const urlToShare = computed(() => `${window.location.origin}${route.fullPath}`);
const isUrlTooBigForQR = computed(() => urlToShare.value.length > 3000);
const qrCodeSize = computed(() => mdAndUp.value ? 400 : 300);

const snackbar = (val) => store.commit('snackbar', val);

function handleToggleTableView() {
  isDotsMenuOpen.value = false;
  url.setResultsView(url.isTableView(route) ? 'list' : 'table');
}

function openQrCode() {
  isDotsMenuOpen.value = false;
  isDialogOpen.qrCode = true;
}

function handleToggleOql() {
  isDotsMenuOpen.value = false;
  emit('toggle-oql');
}
</script>
